import { FaSearch } from '@meronex/icons/fa';
import React from 'react';
import { Spinner } from '@apia/theme';
import Config from 'config';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { downloadUrl } from '../../../../utils/util';
import EventEmitter from '../../../../utils/util/eventEmitter';
import { IconButton, Modal, SimpleButton } from '../../../common';
import useModal from '../../../../hooks/useModal';
import { notify } from '../../../common/notifications/util';
import { TDispatchedNotification } from '@apia/notifications';

export const verifyModalEmitter = new (class extends EventEmitter<{
  show: { url: string };
  hide: boolean;
}> {})();

interface ISignatureData {
  date: string;
  hasPKCS7: boolean;
  user: string;
  userId: number;
}

type TSignaturesData = {
  ajaxUrl: string;
  DownloadLabel: string;
  DownloadToolTip: string;
  docId?: string;
  fldId?: string;
  frmId: number;
  frmParent: string;
  index?: number;
  isDoc?: boolean;
  isField?: boolean;
  noSignaturesLabel: string;
  PKCS7Label: string;
  PKCS7ToolTip: string;
  prefix?: string;
  signatures: ISignatureData[];
  signedByLabel: string;
  signedByToolTip: string;
  signedDateLabel: string;
  signedDateToolTip: string;
  title: string;
  verifyAction: string;
  verifySignLabel: string;
  verifySignToolTip: string;
};

async function checkSignature(
  url: string,
): Promise<TDispatchedNotification | null> {
  const res = await ApiaApi.post<{ text: { label: string } }>(url);

  let message = '';
  let type: TDispatchedNotification['type'] = 'warning';

  if (res) {
    const match = res.data?.text.label.match(
      /<div class="(\w+)">([^<]+)<\/div>/,
    );

    if (match) {
      message = match[2];

      if (match[1] === 'okMsg') type = 'success';
      if (match[1] === 'errMsg') type = 'danger';
      if (match[1] === 'warnMsg') type = 'warning';
    }
  }

  if (message !== '') return { message, type };

  return null;
}

export function useVerifySignModal() {
  const [url, setUrl] = React.useState<string | undefined | null>(undefined);

  React.useEffect(() => {
    if (url) {
      verifyModalEmitter.emit('show', { url });
      setUrl(null);
    } else if (url === undefined) verifyModalEmitter.emit('hide', true);
  }, [url]);

  return setUrl;
}

const VerifySignModal = () => {
  const [data, setSignaturesData] = React.useState<TSignaturesData | undefined>(
    undefined,
  );

  const downloadPKCS7 = React.useCallback(
    (signature: ISignatureData) => {
      downloadUrl(
        makeApiaUrl({
          ajaxUrl: '/apia.execution.FormAction.run',
          action: 'downloadPKCS7',
          signId: signature.userId,
          frmId: data?.frmId,
          frmParent: data?.frmParent,
        }),

        'PKCS7',
      ).catch((e: Error) => {
        notify({
          message: e.toString(),
          type: 'danger',
        });
      });
    },
    [data?.frmId, data?.frmParent],
  );

  const verifySignature = React.useCallback(
    (signature: ISignatureData) => {
      void checkSignature(
        makeApiaUrl({
          ajaxUrl: data?.ajaxUrl,
          action: data?.verifyAction,
          signId: signature.userId,
          frmId: data?.frmId,
          frmParent: data?.frmParent,
          ...(data?.isField
            ? { fldId: data.fldId, index: data.index }
            : undefined),
          ...(data?.isDoc
            ? { docId: data.docId, prefix: data.prefix }
            : undefined),
        }),
      ).then((notification) => {
        if (notification !== null) notify(notification);
      });
    },
    [
      data?.ajaxUrl,
      data?.docId,
      data?.fldId,
      data?.frmId,
      data?.frmParent,
      data?.index,
      data?.isDoc,
      data?.isField,
      data?.prefix,
      data?.verifyAction,
    ],
  );

  const { show, onClose, ...modalProps } = useModal();

  React.useEffect(() => {
    function innerShowModal({ url }: { url: string }) {
      setSignaturesData(undefined);
      show();
      void ApiaApi.get<TSignaturesData>(url).then((res) => {
        if (res?.data) {
          setSignaturesData(res?.data);
        }
      });
    }

    verifyModalEmitter.on('show', innerShowModal);
    verifyModalEmitter.on('hide', onClose);

    return () => {
      void verifyModalEmitter.off('show', innerShowModal);
      void verifyModalEmitter.off('hide', onClose);
    };
  });

  return (
    <Modal
      size="xl"
      {...window.getVariant('layout.execution.components.digitalSign.verify')}
      onClose={onClose}
      title={data?.title}
      id="VerifySignModal"
      {...modalProps}
    >
      {!data && <Spinner />}
      {data && (
        <table>
          <thead>
            <tr>
              <th title={data.signedByToolTip}>{data.signedByLabel}</th>
              <th title={data.signedDateToolTip}>{data.signedDateLabel}</th>
              <th title={data.verifySignToolTip}>{data.verifySignLabel}</th>
              <th title={data.PKCS7ToolTip}>{data.PKCS7ToolTip}</th>
            </tr>
          </thead>
          <tbody>
            {data.signatures.map((signature) => (
              <tr
                key={`${signature.date}${signature.user}${
                  signature.hasPKCS7 ? 'PKCS7' : 'NOPKCS7'
                }`}
              >
                <td>{signature.user}</td>
                <td>{signature.date}</td>
                <td>
                  <IconButton
                    title={Config.BTN_VERIFY_FRM_SIGN}
                    icon={FaSearch}
                    onClick={() => verifySignature(signature)}
                    size={40}
                    {...window.getVariant('icon-outline')}
                  />
                </td>
                <td>
                  {signature.hasPKCS7 && (
                    <SimpleButton
                      {...window.getVariant('outline')}
                      title={data.DownloadToolTip}
                      onClick={() => downloadPKCS7(signature)}
                    >
                      {data.DownloadLabel}
                    </SimpleButton>
                  )}
                </td>
              </tr>
            ))}
            {data.signatures.length === 0 && (
              <tr>
                <td colSpan={4}>{data.noSignaturesLabel}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Modal>
  );
};

export default VerifySignModal;
